import React, { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon, Print as PrintIcon } from "@mui/icons-material";
import { PRINT_DPI_OPTIONS } from "./printLayout";

const BLUE = "#1976d2";

/** print tool drawer panel — Title, Created by (logged-in user), DPI, Print */
export default function PrintToolPanel(ctx) {
  const {
    printTitle,
    setPrintTitle,
    printCreatedBy,
    printDpi,
    setPrintDpi,
    printGenerating,
    printProgress,
    printLogs,
    printError,
    handlePrint,
    onCloseTool,
  } = ctx;

  // log box ko hamesha neeche scroll rakho
  const logRef = useRef(null);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [printLogs]);

  const fmtT = (ms) => `${(ms / 1000).toFixed(1).padStart(5, " ")}s`;

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: BLUE }}>
        Print Map
      </Typography>

      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: 12 }}>
        Title
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Map title"
        value={printTitle}
        onChange={(e) => setPrintTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !printGenerating) handlePrint();
        }}
        inputProps={{ maxLength: 120 }}
        sx={{ mb: 2, bgcolor: "white" }}
      />

      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: 12 }}>
        Created by
      </Typography>
      <TextField
        fullWidth
        size="small"
        value={printCreatedBy || ""}
        disabled
        sx={{ mb: 2, bgcolor: "#f5f5f5" }}
      />

      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: 12 }}>
        Resolution (DPI)
      </Typography>
      <Select
        fullWidth
        size="small"
        value={printDpi}
        onChange={(e) => setPrintDpi(Number(e.target.value))}
        sx={{ mb: 2, bgcolor: "white" }}
      >
        {PRINT_DPI_OPTIONS.map((d) => (
          <MenuItem key={d} value={d}>
            {d} dpi{d === 96 ? " (screen)" : d >= 300 ? " (best)" : ""}
          </MenuItem>
        ))}
      </Select>

      <Button
        fullWidth
        variant="contained"
        onClick={handlePrint}
        disabled={printGenerating}
        startIcon={
          printGenerating ? (
            <CircularProgress size={16} sx={{ color: "white" }} />
          ) : (
            <PrintIcon />
          )
        }
        sx={{ bgcolor: BLUE, "&:hover": { bgcolor: "#115293" } }}
      >
        {printGenerating
          ? printProgress && printProgress.total
            ? `Rendering… ${printProgress.loaded}/${printProgress.total} tiles`
            : "Preparing map…"
          : "Print / Save as PDF"}
      </Button>

      {printError && (
        <Typography
          variant="caption"
          sx={{ fontSize: 11, color: "#f44336", mt: 1, display: "block" }}
        >
          {printError}
        </Typography>
      )}

      <Typography
        variant="caption"
        sx={{ fontSize: 10.5, color: "#888", mt: 1.5, lineHeight: 1.4 }}
      >
        Prints the current map view with visible layers and their legend on an
        A4 landscape sheet. Choose “Save as PDF” in the print dialog to export.
      </Typography>

      {/* Generation log */}
      {(printLogs?.length > 0 || printGenerating) && (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: 12 }}>
            Log
          </Typography>
          <Box
            ref={logRef}
            sx={{
              flex: 1,
              minHeight: 90,
              maxHeight: 220,
              overflow: "auto",
              bgcolor: "#1e1e1e",
              color: "#d4d4d4",
              borderRadius: 1,
              p: 1,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontSize: 10.5,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {printLogs.map((l, i) => (
              <div key={i} style={{ color: l.m.startsWith("✖") ? "#f48771" : l.m.startsWith("Done") ? "#89d185" : undefined }}>
                <span style={{ color: "#808080" }}>{fmtT(l.t)}</span> {l.m}
              </div>
            ))}
            {printGenerating && <div style={{ color: "#808080" }}>…</div>}
          </Box>
        </Box>
      )}

      {!(printLogs?.length > 0 || printGenerating) && <Box sx={{ flex: 1 }} />}
      <Divider sx={{ my: 1.5 }} />
      <Button
        fullWidth
        variant="outlined"
        startIcon={<CloseIcon />}
        onClick={() => onCloseTool("print")}
        sx={{
          borderColor: "#f44336",
          color: "#f44336",
          "&:hover": { bgcolor: "#ffebee" },
        }}
      >
        Close
      </Button>
    </Box>
  );
}
